import { useState, useEffect, useCallback } from 'react'

import { getTagsRequest, createTagRequest } from 'api/tags'
import {
  getActiveMerchantUuid,
  getContextMerchantUuid
} from 'lib/merchant-group'

import { Tag } from 'components/TagsAddInput/TagsAddInput'

// Cache global para tags, por empresa (SC-10999): na visão multi-empresas o
// formulário troca de empresa sem recarregar a página, e as tags de uma não
// podem aparecer na outra.
const tagsCache = new Map<string, { tags: Tag[]; timestamp: number }>()
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutos

const cacheKey = () =>
  getActiveMerchantUuid() || getContextMerchantUuid() || 'context'

interface UseTagsReturn {
  tags: Tag[]
  loading: boolean
  error: string | null
  createTag: (name: string) => Promise<Tag>
  refreshTags: () => Promise<void>
}

const useTags = (): UseTagsReturn => {
  const [tags, setTags] = useState<Tag[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchTags = useCallback(async (forceRefresh = false) => {
    // Verificar se o cache é válido
    const now = Date.now()
    const key = cacheKey()
    const cached = tagsCache.get(key)
    const isCacheValid = cached && now - cached.timestamp < CACHE_DURATION

    if (!forceRefresh && isCacheValid && cached) {
      setTags(cached.tags)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await getTagsRequest({ per_page: 100 })
      const fetchedTags = response.data || []

      // Atualizar cache
      tagsCache.set(key, { tags: fetchedTags, timestamp: now })

      setTags(fetchedTags)
    } catch (err) {
      setError('Erro ao carregar tags')
      console.error('Erro ao buscar tags:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const createTag = useCallback(async (name: string): Promise<Tag> => {
    setError(null)

    try {
      const response = await createTagRequest({
        name: name.trim()
        // A cor será gerada pela API
      })
      const newTag = response.data

      // Invalidar cache ao criar nova tag
      tagsCache.delete(cacheKey())

      setTags((prevTags) => [...prevTags, newTag])
      return newTag
    } catch (err) {
      setError('Erro ao criar tag')
      console.error('Erro ao criar tag:', err)
      throw err
    }
  }, [])

  const refreshTags = useCallback(async () => {
    await fetchTags(true) // Forçar refresh
  }, [fetchTags])

  useEffect(() => {
    fetchTags()
  }, [fetchTags])

  return {
    tags,
    loading,
    error,
    createTag,
    refreshTags
  }
}

export default useTags
